import { useCallback, useEffect, useState } from "react";
import { Loader2, LogOut, RefreshCw, Trash2, Lock, FileDown } from "lucide-react";
import { toast } from "sonner";
import { adminLogin, fetchLeads, fetchStats, deleteLead, exportLeads, apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Logo } from "../components/site/Header";
import { BlogManager } from "../components/admin/BlogManager";

const TOKEN_KEY = "bidc_admin_token";

const TYPE_LABELS = {
  site_visit: "Site Visit",
  brochure: "Brochure",
  channel_partner: "Channel Partner",
  plot_enquiry: "Plot Enquiry",
};

const TYPE_STYLES = {
  site_visit: "bg-forest text-paper",
  brochure: "bg-gold text-forest-dark",
  channel_partner: "bg-clay text-paper",
  plot_enquiry: "bg-forest-light text-paper",
};

const Login = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await adminLogin(password);
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      setError(apiError(err, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-paper px-5">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm border border-border bg-paper-light p-8"
        data-testid="admin-login-form"
      >
        <Logo className="h-14" />
        <h1 className="mt-8 font-display text-2xl font-medium text-forest">Admin — Leads</h1>
        <p className="mt-1 text-sm text-[#4A4C4A]">Enter the admin password to view enquiries.</p>
        <div className="mt-6 space-y-1.5">
          <Label htmlFor="admin-password">Password</Label>
          <Input
            id="admin-password"
            data-testid="admin-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="bg-paper border-border text-ink focus-visible:ring-gold h-11"
            placeholder="••••••••"
          />
        </div>
        {error && (
          <p className="mt-3 text-sm text-red-700" data-testid="admin-login-error">{error}</p>
        )}
        <Button
          type="submit"
          disabled={loading}
          data-testid="admin-login-button"
          className="mt-6 h-11 w-full bg-forest text-paper hover:bg-forest-light transition-colors duration-200"
        >
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Lock className="mr-2 h-4 w-4" />}
          Sign in
        </Button>
      </form>
    </div>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [tab, setTab] = useState("leads");

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await exportLeads(token);
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `bidc-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (err) {
      if (err?.response?.status === 401) logout();
      else toast.error("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  }, []);

  const load = useCallback(
    async (t, f) => {
      setLoading(true);
      try {
        const [leadsRes, statsRes] = await Promise.all([fetchLeads(t, f), fetchStats(t)]);
        setLeads(leadsRes.data);
        setStats(statsRes.data);
      } catch (err) {
        if (err?.response?.status === 401) {
          logout();
        } else {
          toast.error(apiError(err, "Could not load leads"));
        }
      } finally {
        setLoading(false);
      }
    },
    [logout]
  );

  useEffect(() => {
    if (token) load(token, filter);
  }, [token, filter, load]);

  if (!token) return <Login onLogin={setToken} />;

  return (
    <div className="min-h-screen bg-paper" data-testid="admin-dashboard">
      <header className="border-b border-border bg-paper-light">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
          <Logo className="h-12" />
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={exporting}
              data-testid="admin-export-button"
              className="border-forest text-forest hover:bg-forest hover:text-paper"
            >
              {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileDown className="mr-2 h-4 w-4" />}
              Export CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => load(token, filter)}
              data-testid="admin-refresh-button"
              className="border-forest text-forest hover:bg-forest hover:text-paper"
            >
              <RefreshCw className="mr-2 h-4 w-4" /> Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={logout}
              data-testid="admin-logout-button"
              className="border-border text-[#4A4C4A] hover:bg-forest hover:text-paper"
            >
              <LogOut className="mr-2 h-4 w-4" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="font-display text-3xl font-medium text-forest">Dashboard</h1>
          <div className="flex gap-2" data-testid="admin-tabs">
            {[
              ["leads", "Leads"],
              ["blog", "Blog Posts"],
            ].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                data-testid={`tab-${key}`}
                className={`border px-4 py-2 text-sm transition-colors duration-150 ${
                  tab === key
                    ? "border-forest bg-forest text-paper"
                    : "border-border bg-paper-light text-[#4A4C4A] hover:border-forest"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {tab === "blog" && <BlogManager token={token} onAuthFail={logout} />}

        {tab === "leads" && stats && (
          <div className="mt-6 flex flex-wrap gap-3" data-testid="admin-stats">
            <button
              onClick={() => setFilter("all")}
              data-testid="filter-all"
              className={`border px-4 py-2 text-sm transition-colors duration-150 ${
                filter === "all" ? "border-forest bg-forest text-paper" : "border-border bg-paper-light text-[#4A4C4A] hover:border-forest"
              }`}
            >
              All · {stats.total}
            </button>
            {Object.entries(TYPE_LABELS).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                data-testid={`filter-${key}`}
                className={`border px-4 py-2 text-sm transition-colors duration-150 ${
                  filter === key ? "border-forest bg-forest text-paper" : "border-border bg-paper-light text-[#4A4C4A] hover:border-forest"
                }`}
              >
                {label} · {stats.by_type[key] || 0}
              </button>
            ))}
          </div>
        )}

        {tab === "leads" &&
          (loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-gold" />
          </div>
        ) : leads.length === 0 ? (
          <p className="mt-10 border border-dashed border-border bg-paper-light p-10 text-center text-sm text-[#4A4C4A]" data-testid="admin-empty">
            No enquiries yet in this category.
          </p>
        ) : (
          <div className="mt-6 overflow-x-auto border border-border bg-paper-light" data-testid="admin-leads-table">
            <table className="w-full min-w-[900px] text-sm">
              <thead>
                <tr className="border-b border-border bg-sand/60 text-left">
                  <th className="px-4 py-3 font-medium text-forest">When</th>
                  <th className="px-4 py-3 font-medium text-forest">Type</th>
                  <th className="px-4 py-3 font-medium text-forest">Name</th>
                  <th className="px-4 py-3 font-medium text-forest">Phone</th>
                  <th className="px-4 py-3 font-medium text-forest">Company / Industry</th>
                  <th className="px-4 py-3 font-medium text-forest">Interest</th>
                  <th className="px-4 py-3 font-medium text-forest" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-b border-border/60 last:border-0 hover:bg-gold-pale/20" data-testid={`lead-row-${l.id}`}>
                    <td className="px-4 py-3 whitespace-nowrap text-[#4A4C4A]">
                      {new Date(l.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2.5 py-1 text-[11px] font-medium ${TYPE_STYLES[l.type] || "bg-muted"}`}>
                        {TYPE_LABELS[l.type] || l.type}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium text-ink">{l.name}</td>
                    <td className="px-4 py-3">
                      <a href={`tel:+91${l.phone.replace(/\D/g, "").slice(-10)}`} className="text-forest hover:text-gold-dark">
                        {l.phone}
                      </a>
                    </td>
                    <td className="px-4 py-3 text-[#4A4C4A]">
                      {[l.company, l.industry].filter(Boolean).join(" · ") || "—"}
                    </td>
                    <td className="px-4 py-3 text-[#4A4C4A]">
                      {[l.plot_id, l.plot_size, l.preferred_date, l.message].filter(Boolean).join(" · ") || "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        aria-label="Delete lead"
                        data-testid={`delete-lead-${l.id}`}
                        onClick={async () => {
                          try {
                            await deleteLead(token, l.id);
                            toast.success("Lead deleted");
                            load(token, filter);
                          } catch (err) {
                            toast.error(apiError(err, "Delete failed"));
                          }
                        }}
                        className="text-[#4A4C4A]/50 hover:text-red-700 transition-colors duration-150"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          ))}
      </main>
    </div>
  );
}
